import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CircleAlert, FileText, RefreshCw, X } from 'lucide-react';
import LinkifiedText from './LinkifiedText';
import { formatShortDateTime } from '../utils/dates';
import { fetchStoredSummary, generateSummary, summaryNeedsUpdate } from '../utils/ticketSummary';
import './TicketSummaryOverlay.css';

// The ticket « Résumé » on an overlay (28/10), opened from the dashboard
// table: the stored summary at once when there is one (with a note and
// « Mettre à jour » when the conversation moved since, or when it is written
// in another language than the person's), generated when there is none.
// Rendered in a portal: the table's overflow container never clips it, and
// it never navigates away. Escape or a click outside closes it.
const TicketSummaryOverlay = ({ ticketId, subject, onClose }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | generating | ready | error
  const closeRef = useRef(null);

  const generate = useCallback(async () => {
    setPhase('generating');
    try {
      setData(await generateSummary(ticketId));
      setPhase('ready');
    } catch (error) {
      setPhase('error');
    }
  }, [ticketId]);

  useEffect(() => {
    let alive = true;
    setPhase('loading');
    setData(null);
    fetchStoredSummary(ticketId)
      .then((stored) => {
        if (!alive) return;
        if (stored && stored.summary) {
          setData(stored);
          setPhase('ready');
        } else {
          generate();
        }
      })
      .catch(() => { if (alive) generate(); });
    return () => { alive = false; };
  }, [ticketId, generate]);

  // The parent re-renders often (hover tooltips): read its latest onClose
  // without re-running the effect (which would steal the focus each time).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    if (closeRef.current) closeRef.current.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const busy = phase === 'loading' || phase === 'generating';
  const languageName = (code) => (code ? t(`displayPrefs.language_${code}`, code.toUpperCase()) : '');
  const needsUpdate = phase === 'ready' && summaryNeedsUpdate(data);
  const closeLabel = t('ticketSummaryOverlay.close', 'Fermer');

  const overlay = (
    <div
      className="u-modal-backdrop tso-overlay"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="u-modal u-modal--wide tso" role="dialog" aria-modal="true" aria-labelledby="tso-title">
        <div className="u-modal__header">
          <span className="u-icon-chip" aria-hidden="true"><FileText strokeWidth={1.8} /></span>
          <div className="tso__heading">
            <h2 id="tso-title" className="u-modal__title">{t('ticketSummaryOverlay.title', 'Résumé du ticket')}</h2>
            {subject && <p className="u-modal__desc tso__subject" title={subject}>{subject}</p>}
          </div>
          <button
            ref={closeRef}
            type="button"
            className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
            onClick={onClose}
            aria-label={closeLabel}
            title={closeLabel}
          >
            <X strokeWidth={2} />
          </button>
        </div>

        <div className="u-modal__body tso__body">
          {busy && (
            <div className="tso__loading">
              <span className="tso__loading-text" role="status">
                {phase === 'loading'
                  ? t('ticketSummaryOverlay.loading', 'Chargement du résumé…')
                  : t('ticketSummaryOverlay.generating', 'Rédaction du résumé…')}
              </span>
              <span className="u-skeleton tso__line" style={{ width: '62%' }} aria-hidden="true" />
              <span className="u-skeleton tso__line" style={{ width: '88%' }} aria-hidden="true" />
              <span className="u-skeleton tso__line" style={{ width: '74%' }} aria-hidden="true" />
            </div>
          )}

          {phase === 'error' && (
            <div className="u-banner u-banner--danger" role="alert">
              <CircleAlert strokeWidth={2} aria-hidden="true" />
              <div className="u-banner__body">{t('ticketSummaryOverlay.error', "Le résumé n'a pas pu être rédigé. Réessayez.")}</div>
            </div>
          )}

          {phase === 'ready' && data && data.summary && (
            <>
              {data.generated_at && (
                <div className="tso__meta">
                  {data.generated_by
                    ? t('ticketSummaryOverlay.generatedOnBy', 'Généré le {{date}} · demandé par {{name}}',
                      { date: formatShortDateTime(data.generated_at), name: data.generated_by })
                    : t('ticketSummaryOverlay.generatedOn', 'Généré le {{date}}',
                      { date: formatShortDateTime(data.generated_at) })}
                </div>
              )}
              {needsUpdate && (
                <div className="u-banner u-banner--warning" role="note">
                  <CircleAlert strokeWidth={2} aria-hidden="true" />
                  <div className="u-banner__body tso__note-text">
                    {data.stale && (
                      <span>{t('ticketSummaryOverlay.stale', 'De nouveaux messages sont arrivés depuis ce résumé.')}</span>
                    )}
                    {data.language_matches === false && data.language && (
                      <span>
                        {t('ticketSummaryOverlay.otherLanguage', 'Langue de ce résumé : {{language}} — votre préférence : {{preferred}}.',
                          { language: languageName(data.language), preferred: languageName(data.preferred_language) })}
                      </span>
                    )}
                  </div>
                  <div className="u-banner__actions">
                    <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={generate}>
                      {t('ticketSummaryOverlay.update', 'Mettre à jour')}
                    </button>
                  </div>
                </div>
              )}
              <div className="tso__text">
                {data.summary.split('\n').map((line, index) => (
                  <p key={index}>{line ? <LinkifiedText text={line} /> : ' '}</p>
                ))}
              </div>
            </>
          )}

          {phase === 'ready' && (!data || !data.summary) && (
            <div className="u-empty tso__empty">{t('ticketSummaryOverlay.empty', "Rien à résumer pour l'instant.")}</div>
          )}
        </div>

        <div className="u-modal__footer">
          <button type="button" className="u-btn u-btn--secondary" onClick={onClose}>
            {closeLabel}
          </button>
          <button type="button" className="u-btn u-btn--primary" onClick={generate} disabled={busy}>
            <RefreshCw aria-hidden="true" />
            {phase === 'generating'
              ? t('ticketSummaryOverlay.generatingShort', 'Rédaction…')
              : t('ticketSummaryOverlay.regenerate', 'Régénérer')}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default TicketSummaryOverlay;
